import React, { useState, useEffect } from 'react';
import moment from 'moment';
import XLSX from 'xlsx';

// Billing › Invoices: the Excel backup behind an invoice, beside its DocuSign
// PDF (Seth 11/4: "when you click on a specific invoice show the docusign like
// it does now but also have the excel backup for the invoice").
//
// The backups are filed on the job's SharePoint site under 02 - Project
// Controls / 02.04 - Invoicing, one folder per billing period, each workbook
// named with the period it bills: Billing_Analysis_54026071_10.5-10.18.26.xlsx,
// Labor_Analysis_…, and Invoice_Backup_<job>_<M.D.YY>-<M.D.YY>.xlsx from the
// Invoice Workbook's Build invoice folder. readJobInvoiceFiles lists every
// spreadsheet there; `backupsFor` picks the ones whose period is the invoice's.
// Anything under an Archive or "_prior …" folder is an earlier draft: it is
// listed apart, never shown first.
//
// Pure helpers first (tested on their own), then the view.

const ymd = (m) => m.format('YYYY-MM-DD');
const yr = (y) => (String(y).length === 2 ? 2000 + Number(y) : Number(y));

/**
 * The billing period printed in a file or folder name, { from, to } as
 * YYYY-MM-DD, or null: "10.5-10.18.26", "9.28.26-10.4.26", "12.28-1.10.27"
 * (the year rolls back when the start month is after the end month).
 */
export const periodOfName = (name) => {
    const m = /(\d{1,2})\.(\d{1,2})(?:\.(\d{2}|\d{4}))?\s*-\s*(\d{1,2})\.(\d{1,2})\.(\d{2}|\d{4})(?!\d)/.exec(String(name || ''));
    if (!m) return null;
    const toY = yr(m[6]);
    const fromY = m[3] ? yr(m[3]) : (Number(m[1]) > Number(m[4]) ? toY - 1 : toY);
    const from = moment({ year: fromY, month: Number(m[1]) - 1, day: Number(m[2]) });
    const to = moment({ year: toY, month: Number(m[4]) - 1, day: Number(m[5]) });
    if (!from.isValid() || !to.isValid() || to.isBefore(from)) return null;
    return { from: ymd(from), to: ymd(to) };
};

// What a workbook is, off its name; the order they show in.
const KINDS = [
    { re: /invoice[_\s-]*backup/i, label: 'Invoice backup' },
    { re: /billing[_\s-]*analysis/i, label: 'Billing analysis' },
    { re: /labor[_\s-]*analysis/i, label: 'Labor analysis' },
];
export const kindOf = (name) => {
    const i = KINDS.findIndex((k) => k.re.test(String(name || '')));
    return i < 0 ? { rank: KINDS.length, label: String(name || '').replace(/\.\w+$/, '') } : { rank: i, label: KINDS[i].label };
};

// An earlier draft: under an Archive folder, or one named "_prior …" / "_old".
const ARCHIVED = /(^|\/)(archive[^/]*|_[^/]*|old|superseded[^/]*)(\/|$)/i;
export const isArchived = (path) => ARCHIVED.test(String(path || ''));

const days = (a, b) => Math.abs(moment(a, 'YYYY-MM-DD').diff(moment(b, 'YYYY-MM-DD'), 'days'));

/**
 * The spreadsheets that back one invoice. `files` are readJobInvoiceFiles'
 * list; `inv` is { no, replaces, from, to } off the invoice's cover.
 * A file backs the invoice when its name (else its folder's name) carries
 * the invoice's period, up to six days either way (a Monday-to-Sunday
 * workbook on a Sunday-to-Saturday cover, or one run to the end of the
 * week), or a week inside it, or when it names the
 * invoice number. -> { current, older }: `current` the files outside any
 * archive, the full period ahead of a part of it, then by kind, newest
 * first, one per name; `older` the archived copies, newest first.
 */
export const backupsFor = (files, inv) => {
    const nos = [inv?.no, inv?.replaces].filter(Boolean).map((n) => String(n).toLowerCase());
    const hit = (files || []).map((f) => {
        const p = periodOfName(f.name) || periodOfName(String(f.path || '').split('/').pop());
        const named = nos.some((n) => String(f.name || '').toLowerCase().includes(n));
        let fit = null;
        if (p && inv?.from && inv?.to) {
            // The same period, give or take the days to the week's ends
            // (54026070's F00294341 bills 9/21–9/30, its workbook 9.21-10.4.26).
            if (p.from <= inv.to && p.to >= inv.from && days(p.from, inv.from) <= 6 && days(p.to, inv.to) <= 6) fit = 'full';
            else if (p.from >= moment(inv.from, 'YYYY-MM-DD').subtract(1, 'day').format('YYYY-MM-DD')
                && p.to <= moment(inv.to, 'YYYY-MM-DD').add(1, 'day').format('YYYY-MM-DD')) fit = 'part';
        }
        if (named && !fit) fit = 'full';
        return fit ? { ...f, period: p, fit, kind: kindOf(f.name), archived: isArchived(f.path) } : null;
    }).filter(Boolean);
    const newest = (a, b) => String(b.lastModified || '').localeCompare(String(a.lastModified || ''));
    const current = [];
    const seen = new Set();
    hit.filter((f) => !f.archived)
        .sort((a, b) => (a.fit === b.fit ? 0 : a.fit === 'full' ? -1 : 1) || a.kind.rank - b.kind.rank || newest(a, b))
        .forEach((f) => {
            const k = String(f.name).toLowerCase();
            if (!seen.has(k)) { seen.add(k); current.push(f); }
        });
    const older = hit.filter((f) => f.archived || !current.includes(f)).sort(newest);
    return { current, older };
};

const colName = (c) => XLSX.utils.encode_col(c);

/**
 * One sheet as rows of { text, num } for the grid, capped at `maxRows` rows
 * and `maxCols` columns, with trailing blank rows and columns trimmed (an
 * Invoice Backup carries helper formulas pre-filled to row 20,000). ->
 * { rows, cols, widths, more } — `cols` the column indexes shown, `more`
 * true when the sheet runs past the cap.
 */
export const sheetGrid = (ws, maxRows = 1000, maxCols = 50) => {
    if (!ws || !ws['!ref']) return { rows: [], cols: [], widths: [], more: false };
    const rg = XLSX.utils.decode_range(ws['!ref']);
    const lastR = Math.min(rg.e.r, rg.s.r + maxRows - 1);
    const lastC = Math.min(rg.e.c, rg.s.c + maxCols - 1);
    const rows = [];
    let usedR = -1;
    let usedC = -1;
    for (let r = rg.s.r; r <= lastR; r += 1) {
        const row = { r, cells: [] };
        for (let c = rg.s.c; c <= lastC; c += 1) {
            const cell = ws[XLSX.utils.encode_cell({ r, c })];
            const text = cell ? (cell.w != null ? String(cell.w) : cell.v == null ? '' : String(cell.v)) : '';
            if (text.trim()) { usedR = Math.max(usedR, r); usedC = Math.max(usedC, c); }
            row.cells.push({ text, num: cell?.t === 'n' });
        }
        rows.push(row);
    }
    if (usedR < 0) return { rows: [], cols: [], widths: [], more: false };
    const nC = usedC - rg.s.c + 1;
    const cols = Array.from({ length: nC }, (_, i) => rg.s.c + i);
    const widths = cols.map((c) => {
        const wch = ws['!cols']?.[c]?.wch || (ws['!cols']?.[c]?.wpx ? ws['!cols'][c].wpx / 7 : 0);
        return Math.min(Math.max(Math.round((wch || 10) * 7.5), 44), 320);
    });
    return {
        rows: rows.filter((row) => row.r <= usedR).map((row) => ({ ...row, cells: row.cells.slice(0, nC) })),
        cols,
        widths,
        more: rg.e.r > lastR || rg.e.c > lastC,
    };
};

/** A workbook's sheets that are not hidden: [{ name, index }]. */
export const visibleSheets = (wb) => (wb?.SheetNames || [])
    .map((name, index) => ({ name, index, hidden: Boolean(wb.Workbook?.Sheets?.[index]?.Hidden) }))
    .filter((s) => !s.hidden);

// ── View ──

const note = { fontSize: 13, color: 'var(--muted-foreground)', padding: 24, textAlign: 'center' };
const md = (iso) => (iso ? moment(iso).format('M/D/YY h:mm a') : '');

const Grid = ({ ws }) => {
    const g = sheetGrid(ws);
    if (!g.rows.length) return <div style={note}>This sheet is empty.</div>;
    const cellBase = { fontSize: 12, padding: '3px 8px', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
    const head = { ...cellBase, position: 'sticky', top: 0, zIndex: 1, background: 'var(--subtle)', color: 'var(--muted-foreground)', fontWeight: 500, textAlign: 'center' };
    return (
        <>
            <table data-testid="backup-grid" style={{ borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed', background: 'var(--background)' }}>
                <colgroup>
                    <col style={{ width: 44 }} />
                    {g.widths.map((w, i) => <col key={g.cols[i]} style={{ width: w }} />)}
                </colgroup>
                <thead>
                    <tr>
                        <th style={{ ...head, left: 0, zIndex: 2 }} aria-label="Row" />
                        {g.cols.map((c) => <th key={c} style={head}>{colName(c)}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {g.rows.map((row) => (
                        <tr key={row.r}>
                            <td style={{ ...cellBase, position: 'sticky', left: 0, background: 'var(--subtle)', color: 'var(--muted-foreground)', textAlign: 'right' }}>{row.r + 1}</td>
                            {row.cells.map((cell, i) => (
                                <td
                                    key={g.cols[i]}
                                    title={cell.text.length > 14 ? cell.text : undefined}
                                    style={{ ...cellBase, color: 'var(--foreground)', textAlign: cell.num ? 'right' : 'left', fontVariantNumeric: cell.num ? 'tabular-nums' : undefined }}
                                >
                                    {cell.text}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
            {g.more ? <div style={{ fontSize: 12, color: 'var(--muted-foreground)', padding: '8px 12px' }}>The sheet runs on past what is shown here — download it or open it in Excel for the rest.</div> : null}
        </>
    );
};

/**
 * The backup side of the invoice panel. `backups` is { loading, error,
 * folderUrl, current, older } (backupsFor over readJobInvoiceFiles);
 * `file` the one picked and `onPick` changes it; `loadBook(file)` resolves
 * to { wb, url } (the parsed workbook and a download URL), cached by the
 * caller.
 */
export const InvoiceBackup = ({ backups, file, onPick, loadBook, hasPeriod }) => {
    const [book, setBook] = useState({ id: '', wb: null, error: '' });
    const [sheet, setSheet] = useState(0);
    const fileId = file?.id || '';

    useEffect(() => {
        if (!file) return undefined;
        let stale = false;
        setBook({ id: file.id, wb: null, error: '' });
        loadBook(file)
            .then((d) => {
                if (stale) return;
                setBook({ id: file.id, wb: d.wb, error: '' });
                setSheet(visibleSheets(d.wb)[0]?.index || 0);
            })
            .catch((e) => { if (!stale) setBook({ id: file.id, wb: null, error: e?.message || String(e) }); });
        return () => { stale = true; };
    }, [fileId]);

    if (backups.loading) return <div style={note}>Looking for the Excel backup on SharePoint…</div>;
    if (backups.error) return <div style={{ ...note, color: 'var(--destructive-text)' }}>{`The invoicing folder could not be read from SharePoint — ${backups.error}`}</div>;
    if (!file) {
        return (
            <div style={note}>
                {hasPeriod
                    ? 'No Excel backup for this invoice’s period is filed in the job’s 02.04 - Invoicing folder on SharePoint.'
                    : 'This invoice’s billing period has not been read off its cover yet, so no backup can be matched to it.'}
                {backups.folderUrl ? (
                    <div style={{ marginTop: 8 }}>
                        <a href={backups.folderUrl} target="_blank" rel="noopener noreferrer">Open the invoicing folder</a>
                    </div>
                ) : null}
            </div>
        );
    }
    const all = [...backups.current, ...backups.older];
    const sheets = book.wb ? visibleSheets(book.wb) : [];
    return (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0, minHeight: 0 }}>
            {all.length > 1 ? (
                <div style={{ display: 'flex', alignItems: 'center', columnGap: 8, rowGap: 6, flexWrap: 'wrap', padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--background)' }}>
                    <div role="group" aria-label="Backup file" style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 2, padding: 3, background: 'var(--muted)', borderRadius: 'var(--radius)' }}>
                        {backups.current.map((f) => (
                            <button
                                key={f.id}
                                type="button"
                                aria-pressed={f.id === file.id}
                                onClick={() => onPick(f)}
                                title={f.name}
                                style={{
                                    font: 'inherit', fontSize: 13, fontWeight: 500, padding: '4px 12px', borderRadius: 'var(--radius-inner)', border: 'none', cursor: 'pointer',
                                    background: f.id === file.id ? 'var(--primary)' : 'transparent', color: f.id === file.id ? 'var(--primary-foreground)' : 'var(--text-secondary)',
                                }}
                            >
                                {f.kind.label}{f.fit === 'part' && f.period ? ` ${moment(f.period.from).format('M/D')}–${moment(f.period.to).format('M/D')}` : ''}
                            </button>
                        ))}
                    </div>
                    {backups.older.length ? (
                        <select
                            aria-label="Earlier drafts"
                            value={backups.older.some((f) => f.id === file.id) ? file.id : ''}
                            onChange={(ev) => { const f = backups.older.find((x) => x.id === ev.target.value); if (f) onPick(f); }}
                            style={{ font: 'inherit', fontSize: 13, height: 32, padding: '0 8px', borderRadius: 'var(--radius)', border: '1px solid var(--input)', background: 'var(--background)', color: 'var(--foreground)', maxWidth: 260 }}
                        >
                            <option value="">{`Earlier drafts (${backups.older.length})`}</option>
                            {backups.older.map((f) => <option key={f.id} value={f.id}>{`${f.kind.label} · ${md(f.lastModified)} · ${f.path}`}</option>)}
                        </select>
                    ) : null}
                </div>
            ) : null}
            <div style={{ fontSize: 12, color: 'var(--muted-foreground)', padding: '6px 16px', background: 'var(--background)', borderBottom: '1px solid var(--border)', wordBreak: 'break-word' }}>
                {`${file.path ? `${file.path} / ` : ''}${file.name}${file.lastModified ? ` · saved ${md(file.lastModified)}` : ''}${file.archived ? ' · earlier draft' : ''}`}
            </div>
            {sheets.length > 1 ? (
                <div role="tablist" aria-label="Sheets" style={{ display: 'flex', overflowX: 'auto', borderBottom: '1px solid var(--border)', background: 'var(--background)', scrollbarWidth: 'thin', scrollbarColor: 'var(--border) transparent' }}>
                    {sheets.map((s) => (
                        <button
                            key={s.index}
                            type="button"
                            role="tab"
                            aria-selected={s.index === sheet}
                            onClick={() => setSheet(s.index)}
                            style={{
                                font: 'inherit', fontSize: 13, padding: '8px 12px', background: 'transparent', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                                borderBottom: `2px solid ${s.index === sheet ? 'var(--destructive)' : 'transparent'}`,
                                color: s.index === sheet ? 'var(--foreground)' : 'var(--muted-foreground)', fontWeight: s.index === sheet ? 600 : 400,
                            }}
                        >
                            {s.name}
                        </button>
                    ))}
                </div>
            ) : null}
            <div style={{ flexGrow: 1, minHeight: 0, overflow: 'auto', scrollbarWidth: 'thin', scrollbarColor: 'var(--border) transparent' }}>
                {book.wb ? <Grid key={`${book.id}-${sheet}`} ws={book.wb.Sheets[book.wb.SheetNames[sheet]]} />
                    : book.error ? <div style={{ ...note, color: 'var(--destructive-text)' }}>{`The workbook could not be read — ${book.error}`}</div>
                        : <div style={note}>Reading the workbook from SharePoint…</div>}
            </div>
        </div>
    );
};

